import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { 
  collection, 
  query, 
  where, 
  onSnapshot,
  doc,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  arrayUnion,
  serverTimestamp
} from 'firebase/firestore';
import toast from 'react-hot-toast';

const WorkspaceContext = createContext();

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
};

export const WorkspaceProvider = ({ children }) => {
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [privacyMode, setPrivacyMode] = useState('team');
  const [loading, setLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);

  // Initialize personal workspace for the current user
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      setLoading(false);
      return;
    }

    const initializePersonalWorkspace = async () => {
      try {
        // Get or create personal workspace
        const personalWorkspaceRef = doc(db, 'workspaces', `personal_${user.uid}`);
        const personalWorkspace = await getDoc(personalWorkspaceRef);

        if (!personalWorkspace.exists()) {
          // Create personal workspace if it doesn't exist
          const personalData = {
            id: `personal_${user.uid}`,
            name: 'Personal',
            type: 'personal',
            ownerId: user.uid,
            members: [user.uid],
            createdAt: serverTimestamp(),
            avatar: user.displayName ? user.displayName.substring(0, 2).toUpperCase() : 'ME'
          };

          await setDoc(personalWorkspaceRef, personalData);
          setCurrentWorkspace(personalData);
        } else {
          setCurrentWorkspace({
            id: personalWorkspace.id,
            ...personalWorkspace.data()
          });
        }
      } catch (error) {
        // Silently ignore workspace initialization errors
        // This can happen due to Firestore permission issues that don't affect core functionality
      }
    };

    initializePersonalWorkspace();
  }, []);

  // Load user's workspaces
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    // Query for all workspaces where user is a member
    const workspacesQuery = query(
      collection(db, 'workspaces'),
      where('members', 'array-contains', user.uid)
    );

    const unsubscribe = onSnapshot(workspacesQuery, (snapshot) => {
      const workspacesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Separate personal and organization workspaces
      const personal = workspacesList.find(w => w.type === 'personal' && w.ownerId === user.uid);
      const organizations = workspacesList.filter(w => w.type === 'organization');

      setWorkspaces(organizations);
      
      // If no current workspace, set to personal
      if (!currentWorkspace && personal) {
        setCurrentWorkspace(personal);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentWorkspace]);

  // Load team members for current organization
  useEffect(() => {
    if (!currentWorkspace || currentWorkspace.type !== 'organization') {
      setTeamMembers([]);
      return;
    }

    const loadTeamMembers = async () => {
      try {
        const memberPromises = currentWorkspace.members.map(async (memberId) => {
          const userDoc = await getDoc(doc(db, 'users', memberId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            return {
              id: memberId,
              name: userData.displayName || userData.email,
              email: userData.email,
              avatar: userData.photoURL,
              initials: userData.displayName ? 
                userData.displayName.split(' ').map(n => n[0]).join('').toUpperCase() : 
                userData.email[0].toUpperCase(),
              role: currentWorkspace.roles?.[memberId] || 'member',
              isOnline: userData.lastActive && 
                (Date.now() - userData.lastActive.toMillis()) < 5 * 60 * 1000 // Active in last 5 mins
            };
          }
          return null;
        });

        const members = await Promise.all(memberPromises);
        setTeamMembers(members.filter(m => m !== null));
      } catch (error) {
        console.error('Error loading team members:', error);
      }
    };

    loadTeamMembers();
  }, [currentWorkspace]);

  // Save privacy mode preference
  useEffect(() => {
    if (privacyMode) {
      localStorage.setItem('klorah_privacy_mode', privacyMode);
    }
  }, [privacyMode]);

  // Load privacy mode preference
  useEffect(() => {
    const savedMode = localStorage.getItem('klorah_privacy_mode');
    if (savedMode) {
      setPrivacyMode(savedMode);
    }
  }, []);

  const switchWorkspace = async (workspace) => {
    setCurrentWorkspace(workspace);
    
    // Default to team view when switching to organization
    if (workspace.type === 'organization') {
      setPrivacyMode('team');
    } else {
      setPrivacyMode('personal');
    }
    
    toast.success(`Switched to ${workspace.name}`);
  };

  const createOrganization = async (organizationData) => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      const newOrg = {
        ...organizationData,
        type: 'organization',
        ownerId: user.uid,
        members: [user.uid],
        roles: {
          [user.uid]: 'admin'
        },
        createdAt: serverTimestamp(),
        plan: 'team', // Default plan
        settings: {
          allowInvites: true,
          defaultVisibility: 'organization'
        }
      };

      const docRef = await addDoc(collection(db, 'workspaces'), newOrg);
      
      // Update user document with new organization
      await updateDoc(doc(db, 'users', user.uid), {
        organizations: arrayUnion(docRef.id)
      });

      toast.success(`Created organization: ${organizationData.name}`);
      
      // Switch to new organization
      switchWorkspace({
        id: docRef.id,
        ...newOrg
      });

      return docRef.id;
    } catch (error) {
      console.error('Error creating organization:', error);
      toast.error('Failed to create organization');
      throw error;
    }
  };

  const inviteToOrganization = async (email, role = 'member') => {
    if (!currentWorkspace || currentWorkspace.type !== 'organization') {
      toast.error('Can only invite to organizations');
      return;
    }

    try {
      // Create invitation document
      await addDoc(collection(db, 'invitations'), {
        organizationId: currentWorkspace.id,
        organizationName: currentWorkspace.name,
        invitedEmail: email,
        invitedBy: auth.currentUser.uid,
        role: role,
        status: 'pending',
        createdAt: serverTimestamp()
      });

      // TODO: Send email invitation
      toast.success(`Invitation sent to ${email}`);
    } catch (error) {
      console.error('Error sending invitation:', error);
      toast.error('Failed to send invitation');
    }
  };

  const value = {
    currentWorkspace,
    workspaces,
    privacyMode,
    teamMembers,
    loading,
    setPrivacyMode,
    switchWorkspace,
    createOrganization,
    inviteToOrganization,
    isPersonalWorkspace: currentWorkspace?.type === 'personal',
    isOrganizationWorkspace: currentWorkspace?.type === 'organization'
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export default WorkspaceContext;